import { CheckCircle, AlertCircle, Clock, XCircle, Ban } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';

type AccountInformationCardProps = {
  accountStatus: 'ACTIVE' | 'INACTIVE' | 'SUSPENDED' | 'PENDING_VERIFICATION';
  vettingStatus: 'PENDING' | 'IN_REVIEW' | 'APPROVED' | 'REJECTED';
};

export function AccountInformationCard({
  accountStatus: accountStatusValue,
  vettingStatus
}: AccountInformationCardProps) {
  // Account Status Logic - Direct mapping from database field
  const getAccountStatus = () => {
    switch (accountStatusValue) {
      case 'ACTIVE':
        return {
          icon: CheckCircle,
          text: 'Active',
          color: 'text-green-600',
        };
      case 'INACTIVE':
        return {
          icon: AlertCircle,
          text: 'Inactive',
          color: 'text-gray-600',
        };
      case 'SUSPENDED':
        return {
          icon: Ban,
          text: 'Suspended',
          color: 'text-red-600',
        };
      case 'PENDING_VERIFICATION':
        return {
          icon: Clock,
          text: 'Pending Verification',
          color: 'text-yellow-600',
        };
      default:
        return {
          icon: AlertCircle,
          text: 'Unknown',
          color: 'text-gray-600',
        };
    }
  };

  const accountStatus = getAccountStatus();

  // Vetting Status Logic
  const getVettingStatus = () => {
    switch (vettingStatus) {
      case 'APPROVED':
        return {
          icon: CheckCircle,
          text: 'Approved',
          color: 'text-green-600',
          message: null,
        };
      case 'IN_REVIEW':
        return {
          icon: Clock,
          text: 'In Review',
          color: 'text-blue-600',
          message: 'Verification in progress',
        };
      case 'PENDING':
        return {
          icon: AlertCircle,
          text: 'Pending',
          color: 'text-gray-600',
          message: 'Your profile is under review',
        };
      case 'REJECTED':
        return {
          icon: XCircle,
          text: 'Rejected',
          color: 'text-red-600',
          message: (
            <>
              Verification failed -{' '}
              <a
                href="mailto:support@bquest.com"
                className="text-blue-600 hover:underline"
              >
                contact support
              </a>
            </>
          ),
        };
      default:
        // Fallback to PENDING for any unexpected value
        return {
          icon: AlertCircle,
          text: 'Pending',
          color: 'text-gray-600',
          message: 'Your profile is under review',
        };
    }
  };

  const vettingStatusInfo = getVettingStatus();
  const AccountStatusIcon = accountStatus.icon;
  const VettingStatusIcon = vettingStatusInfo.icon;

  return (
    <Card className="p-3 w-full">
      <h3 className="text-sm font-semibold mb-2 text-gray-900">Account Information</h3>

      <div className="flex gap-4">
        {/* Account Status */}
        <div>
          <label className="text-xs font-medium text-gray-700 block mb-1">
            Account Status
          </label>
          <div className={`flex items-center gap-1.5 ${accountStatus.color}`}>
            <AccountStatusIcon className="w-4 h-4" />
            <span className="text-sm whitespace-nowrap">{accountStatus.text}</span>
          </div>
        </div>

        {/* Vetting Status */}
        <div>
          <label className="text-xs font-medium text-gray-700 block mb-1">
            Vetting Status
          </label>
          <div className={`flex items-center gap-1.5 ${vettingStatusInfo.color}`}>
            <VettingStatusIcon className="w-4 h-4" />
            <span className="text-sm whitespace-nowrap">{vettingStatusInfo.text}</span>
          </div>
        </div>
      </div>

      {/* Message below both statuses if exists */}
      {vettingStatusInfo.message && (
        <p className="text-xs text-gray-600 mt-2">
          {vettingStatusInfo.message}
        </p>
      )}
    </Card>
  );
}
